import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { Link } from 'react-router-dom';
import logo from '../logo.png';
import { APP_STORE_URL } from '../constants';

const Navbar = () => {
    const { getFullTranslation, language, setLanguage, languageNames, availableLanguages } = useLanguage();
    const nav = getFullTranslation('nav');
    const [isLangOpen, setIsLangOpen] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    const toggleLangMenu = () => {
        setIsLangOpen(!isLangOpen);
    };

    const selectLanguage = (lang) => {
        setLanguage(lang);
        setIsLangOpen(false);
    };

    return (
        <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    {/* Logo & Brand */}
                    <div className="flex items-center space-x-8">
                        <Link to="/" className="flex items-center space-x-3 group">
                            <img src={logo} alt="食光机" className="w-10 h-10 rounded-xl shadow-sm group-hover:scale-105 transition-transform" />
                            <span className="font-bold text-xl tracking-tight text-gray-900 group-hover:text-orange-600 transition-colors">食光机</span>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex space-x-6">
                            <Link to="/" className="text-gray-600 hover:text-orange-600 font-medium transition-colors text-sm">{nav.home}</Link>
                            <Link to="/features" className="text-gray-600 hover:text-orange-600 font-medium transition-colors text-sm">{nav.features}</Link>
                            <a href="/#download" className="text-gray-600 hover:text-orange-600 font-medium transition-colors text-sm">{nav.download}</a>
                            <Link to="/subscription" className="text-gray-600 hover:text-orange-600 font-medium transition-colors text-sm">{nav.premium}</Link>
                            <a href="/#faq" className="text-gray-600 hover:text-orange-600 font-medium transition-colors text-sm">{nav.faq}</a>
                        </div>
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center space-x-4">
                        {/* Language Selector */}
                        <div className="relative hidden sm:block">
                            <button
                                onClick={toggleLangMenu}
                                className="flex items-center space-x-1 text-gray-600 hover:text-orange-600 hover:bg-orange-50 px-3 py-2 rounded-lg transition-colors text-sm font-medium"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path></svg>
                                <span>{languageNames[language]}</span>
                                <svg className={`w-4 h-4 transition-transform ${isLangOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                            </button>

                            {/* Dropdown Menu */}
                            {isLangOpen && (
                                <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                                    {availableLanguages.map((lang) => (
                                        <button
                                            key={lang}
                                            onClick={() => selectLanguage(lang)}
                                            className={`w-full text-left px-4 py-2 text-sm transition-colors hover:bg-orange-50 ${language === lang ? 'text-orange-600 font-medium bg-orange-50/50' : 'text-gray-700'
                                                }`}
                                        >
                                            {languageNames[lang]}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* CTA Button */}
                        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-xl font-medium text-sm transition-all shadow-md shadow-orange-500/20">
                            {nav.downloadNow}
                        </a>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileOpen(!isMobileOpen)}
                            className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isMobileOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                                )}
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Dropdown */}
            {isMobileOpen && (
                <div className="md:hidden border-t border-gray-100 bg-white shadow-lg">
                    <div className="px-4 pt-2 pb-6 space-y-1">
                        <Link to="/" onClick={() => setIsMobileOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg">{nav.home}</Link>
                        <Link to="/features" onClick={() => setIsMobileOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg">{nav.features}</Link>
                        <a href="/#download" onClick={() => setIsMobileOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg">{nav.download}</a>
                        <Link to="/subscription" onClick={() => setIsMobileOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg">{nav.premium}</Link>
                        <a href="/#faq" onClick={() => setIsMobileOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg">{nav.faq}</a>

                        {/* Mobile Language Selector */}
                        <div className="pt-4 mt-2 border-t border-gray-100">
                            <div className="px-3 text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">Language</div>
                            <div className="flex flex-wrap gap-2 px-3">
                                {availableLanguages.map((lang) => (
                                    <button
                                        key={lang}
                                        onClick={() => { selectLanguage(lang); setIsMobileOpen(false); }}
                                        className={`px-4 py-2 text-sm rounded-lg border flex-1 text-center transition-colors ${language === lang ? 'border-orange-500 bg-orange-50 text-orange-600 font-bold' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        {languageNames[lang]}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Mobile Download Button */}
                        <div className="pt-6 px-3">
                            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-full bg-orange-600 hover:bg-orange-700 text-white px-5 py-3.5 rounded-xl font-bold text-base transition-all shadow-md shadow-orange-500/20">
                                {nav.downloadNow}
                            </a>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
